import { BehaviorSubject, defer, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { catchError, debounceTime, exhaustMap, filter, retry, share } from 'rxjs'
import { OAuth2Client } from '@byteowls/capacitor-oauth2'
import { SecureStoragePlugin, WebStorageAdapter } from '@atroo/capacitor-secure-storage-plugin'
import { EventType, getEventSubscription, NotificationType } from '@obeta/utils/lib/pubSub'
import { RxDatabase } from 'rxdb'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { AppActions, Tokens } from '@obeta/models/lib/models/BusinessLayer/AppActions'
import { CustomerMetaData } from '@obeta/models/lib/models/Users/UserV2'
import {
  APP_ID,
  AUTH_BASE_URL,
  ELDIS_IFRAME_URL,
  HAEUSLER_IFRAME_URL,
  OBETA_IFRAME_URL,
} from '@obeta/utils/lib/config'
import { isPlatform } from '@obeta/utils/lib/isPlatform'
import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel'
import { resync } from './resync'
import { CapacitorHttp, HttpResponse } from '@capacitor/core'
import { deleteUserToken } from '@obeta/data/lib/actions/customer-actions'
import { datadogRum } from '@datadog/browser-rum'
import { Store } from 'redux'
import { clearSessionContext, getSessionContext } from '@obeta/utils/lib/session-context'
import { getOauthWebRedirectUrl } from '@obeta/data/lib/hooks/oath2/utils'
import { channelTokenReceive } from '@obeta/data/lib/hooks/useLogin'
import { sessionStorageInitialTokenChannel } from './bootstrap'

const tokenRefreshSubject$ = new Subject()
const logout$ = new Subject()
export const tokens$ = new BehaviorSubject<Tokens | null>(null)

let refreshToken: string | undefined

interface MessageTokenRefresh {
  action: 'refresh_token'
}

interface MessageShopUpdate {
  action: 'version_change'
  storageKey: string
  storageValue: string
}

interface MessageLogoutRunning {
  action: 'logout_running'
  phase: 'started' | 'ended'
}

class HydraNotReachableError extends Error {
  constructor(message) {
    super(message)
    this.name = 'HydraNotReachableError'
  }
}

export const channelLogoutRunning = new BroadcastChannel<MessageLogoutRunning>('logout_running', {
  type: typeof window === 'undefined' ? 'simulate' : 'native',
})

interface AppActionsConfig {
  /** we need this in the print service as there is only one tab anyhow, and there is no need to fire up the nodejs filesystem based broadcast channel */
  disableMultiTabRefresh?: boolean
}

export const initAppActions = (
  db: RxDatabase,
  tokens: Tokens | null = null,
  store?: Store,
  config?: AppActionsConfig
): AppActions => {
  if (tokens) {
    tokens$.next(tokens)
  }

  let tokensForInit = tokens
  tokens$.subscribe((tokens) => {
    tokensForInit = tokens
    if (tokens) {
      refreshToken = tokens.refreshToken
    } else {
      refreshToken = undefined
    }
  })

  const channelShopVersion = new BroadcastChannel<MessageShopUpdate>('shop_version')
  const channelTokenRefresh = new BroadcastChannel<MessageTokenRefresh>('token_refresh')

  let elector: LeaderElector | undefined
  if (config?.disableMultiTabRefresh !== true) {
    elector = createLeaderElection(channelTokenRefresh)

    elector.awaitLeadership().then(() => {
      // eslint-disable-next-line no-console
      console.log('this tab is now leader')
      datadogRum.addAction('Leadership acquired')
    })

    channelTokenRefresh.onmessage = (data) => {
      if (data.action === 'refresh_token' && (elector as LeaderElector).isLeader) {
        tokenRefreshSubject$.next(true)
      }
    }
  }

  channelTokenReceive.onmessage = async (data) => {
    if (data.action === 'token_received' && data.tokens) {
      tokens$.next(data.tokens)
      //only session storag is not shared, if we write to local storage we dont need to set it explicitly
      if (
        (await SecureStoragePlugin.getStorageAdapter()).adapter === WebStorageAdapter.SessionStorage
      ) {
        SecureStoragePlugin.set({
          key: 'auth',
          value: JSON.stringify(data.tokens),
          accessibility: 'afterFirstUnlock',
        })
      }
    }
  }

  sessionStorageInitialTokenChannel.onmessage = async (data) => {
    if (data.action === 'token_requested' && tokensForInit) {
      sessionStorageInitialTokenChannel.postMessage({
        action: 'token_received',
        tokens: tokensForInit,
      })
    }
  }

  channelShopVersion.onmessage = (data) => {
    if (typeof window === 'undefined' || !localStorage) return
    if (data.action === 'version_change') {
      localStorage.setItem(data.storageKey, data.storageValue)
      // eslint-disable-next-line no-console
      console.log(`reload tab and store new shop version: ${data.storageValue}`)
      window.location.reload()
    }
  }

  tokens$
    .pipe(
      filter((tokens) => !!tokens),
      exhaustMap(() =>
        defer(async () => {
          if (!isPlatform('web')) {
            const result = await FirebaseMessaging.checkPermissions()
            if (result.receive === 'granted') {
              // Register with Apple / Google to receive push via APNS/FCM
              try {
                await FirebaseMessaging.getToken()
              } catch (err) {
                // Sentry.captureException(err) TODO replace former Sentry-Code with DataDog
              }
            }
          }
        }).pipe(
          catchError((error: Error) => {
            /*Sentry.addBreadcrumb({
              category: 'push',
              message: 'Error registering for push notifications',
            })
            Sentry.captureException(error) */ // TODO replace former Sentry-Code with DataDog
            return of(null)
          })
        )
      ),
      share()
    )
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    .subscribe(() => {})

  const logoutFinished$ = logout$.pipe(
    exhaustMap(() =>
      defer(async () => {
        datadogRum.addAction('logging user out')
        channelLogoutRunning.postMessage({
          action: 'logout_running',
          phase: 'started',
        })

        const userMeta = await db.getLocal<CustomerMetaData>('usermeta')
        const isLoggedIn = userMeta?.get('isLoggedIn')
        // Clear push notification tokens before clearing auth tokens.
        if (isLoggedIn) {
          if (!isPlatform('web')) {
            try {
              await FirebaseMessaging.deleteToken()
              if (store) await store.dispatch(deleteUserToken())
            } catch (err) {
              datadogRum.addAction('Failed to unregister from firebase')
              datadogRum.addError(err)
            }
          }
        }

        let idToken, accessToken
        try {
          const { value } = await SecureStoragePlugin.get({ key: 'auth' })
          if (value) {
            const authObj: Tokens = JSON.parse(value)
            idToken = authObj.idToken
            accessToken = authObj.accessToken
          }
          clearSessionContext()
          // Clear auth tokens.
          // Otherwise these tokens will be used to authenticate user (check useLogin)
          await SecureStoragePlugin.clear()
        } catch (err) {
          datadogRum.addAction('Failed to clear secure storage')
          datadogRum.addError(err)
        }

        try {
          await OAuth2Client.logout({ appId: APP_ID })
        } catch (err) {
          datadogRum.addAction('failed to clear oauth2 session')
          datadogRum.addError(err)
        }

        tokens$.next(null)
        clearSessionContext()
        resync.cancelReplications()
        try {
          datadogRum.addAction('resetting local state')
          await db.remove()
        } catch (err) {
          datadogRum.addAction('Failed to reset local state')
          datadogRum.addError(err)
        }

        try {
          //the following only needs to be done for web, elswise the session will be destiryd by the plugin above
          if (isPlatform('web')) {
            if (idToken) {
              //we only perform the session destruction hydra side if we have an idToken - this will not be the case for sessions that have been created before using xdomain logins
              const redirectUrl: string = getOauthWebRedirectUrl({
                withDeeplinkRestoring: false, //for logouts we can end up on the start page
              })

              //to destroy a potential "rememberMe" session we need to let hydra know that the user left wants the token to be destroyed
              //we do this in a frame as we dont need the response and want to continue cleaning up in parallel
              const logoutUrl =
                AUTH_BASE_URL +
                '/oauth2/sessions/logout?' +
                new URLSearchParams({
                  post_logout_redirect_uri: redirectUrl,
                  id_token_hint: idToken,
                })

              channelLogoutRunning.postMessage({
                action: 'logout_running',
                phase: 'ended',
              })
              //this kills the session in potentially authe iframe urls as well. can be removed when classic shop is sunset
              const Routes: Record<string, string> = {
                '1010': OBETA_IFRAME_URL,
                '1030': HAEUSLER_IFRAME_URL,
                '1020': ELDIS_IFRAME_URL,
              }

              const companyId = userMeta?.get('companyId')
              if (companyId) {
                //fire and forget as this
                fetch(`${Routes[companyId]}/logout?embedMode=1&token=${accessToken}`).catch(() => {
                  datadogRum.addError('logout could not destroy legacy shop session')
                })
              }
              //
              window.location.replace(logoutUrl)
              //
            } else {
              /**
               * there are two cases that need this logic down here
               * 1. if users are redirected from antoher shop, we intentionally clean all login information first, and the do reload --> open tabs need to now about this end too
               * 2. users that where loggedin before using the new flow, might not have an idToken at all, and therefore also need to reload
               */
              channelLogoutRunning.postMessage({
                action: 'logout_running',
                phase: 'ended',
              })
              window.location.reload()
            }
          } else {
            /**
             * if we are logging out, we should end up on the start page to not trigger cyclic logins when logging off on a authenticated only view
             */
            window.location.replace('/')
          }
        } catch (err) {
          datadogRum.addAction('failed to delete hydra cookie')
          datadogRum.addError(err)
        }
      }).pipe(
        catchError((error) => {
          datadogRum.addAction('Failed to reset local state')
          datadogRum.addError(error)

          return of()
        })
      )
    ),
    share()
  )

  logoutFinished$.subscribe()

  const requestTokens$: Observable<boolean> = tokenRefreshSubject$.pipe(
    debounceTime(1000),
    exhaustMap(() =>
      defer(async () => {
        let tokens: Tokens | null = null
        const isElectorHasLeader = await elector?.hasLeader()

        datadogRum.addAction('before tab election ', {
          isLeader: elector?.isLeader || true,
          hasLeader: isElectorHasLeader || true,
        })

        // we need a new token, but are not the leading tab
        // inform the leader to refresh the token and wait for the result
        if (!elector?.isLeader && isElectorHasLeader && isPlatform('web')) {
          channelTokenRefresh.postMessage({ action: 'refresh_token' })
          return false
        }

        datadogRum.addAction('initiate token refresh ', {
          hasRefreshToken: !!refreshToken,
        })

        if (isPlatform('web')) {
          const sessionContext = getSessionContext()
          if (sessionContext) return false

          const form = new FormData()
          form.append('refresh_token', refreshToken as string)
          form.append('grant_type', 'refresh_token')
          form.append('client_id', APP_ID as string)

          let tokenSet
          try {
            const response = await fetch(AUTH_BASE_URL + '/oauth2/token', {
              method: 'POST',
              body: form,
            })
            tokenSet = await response.json()
          } catch (err) {
            // if fetch throws an error, it is considered a network error
            // service is down or no network connectivity
            // in this case, we should not log the user out
            throw new HydraNotReachableError(err.message)
          }
          tokens = {
            accessToken: tokenSet['access_token'],
            refreshToken: tokenSet['refresh_token'],
            idToken: tokenSet['id_token'],
          }
        } else {
          const sessionContext = getSessionContext()
          if (sessionContext) return false
          // temporary solution until Capacitor Http can process FormData
          // @see https://github.com/ionic-team/capacitor/pull/6206
          const formData = {
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
            client_id: APP_ID as string,
          }
          const formBody: string[] = []
          for (const prop in formData) {
            const encodedKey = encodeURIComponent(prop)
            const encodedValue = encodeURIComponent(formData[prop])
            formBody.push(encodedKey + '=' + encodedValue)
          }
          const form = formBody.join('&')

          const options = {
            url: AUTH_BASE_URL + '/oauth2/token',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            data: form,
          }

          let tokenSet
          try {
            const response: HttpResponse = await CapacitorHttp.post(options)
            tokenSet = response.data

            datadogRum.addAction('token refresh response', {
              statusCode: response.status,
              hasAccessToken: !!tokenSet['access_token'],
              hasRefreshToken: !!tokenSet['refresh_token'],
              response: response.status !== 200 ? response : undefined,
            })
          } catch (err) {
            // if fetch throws an error, it is considered a network error
            // service is down or no network connectivity
            // in this case, we should not log the user out
            throw new HydraNotReachableError(err.message)
          }

          tokens = {
            accessToken: tokenSet['access_token'],
            refreshToken: tokenSet['refresh_token'],
            idToken: tokenSet['id_token'],
          }
        }

        if (tokens.refreshToken && tokens.accessToken) {
          datadogRum.addAction('received new tokens')

          // inform components about new tokens
          tokens$.next(tokens)

          // inform other tabs about new tokens
          channelTokenReceive.postMessage({
            action: 'token_received',
            tokens: tokens,
          })
          try {
            await SecureStoragePlugin.set({
              key: 'auth',
              value: JSON.stringify(tokens),
              accessibility: 'afterFirstUnlock',
            })
            datadogRum.addAction('stored new tokens')
          } catch (err) {
            datadogRum.addError(err)

            // if we cannot store the token, we should indicate that
            return false
          }

          return true
        } else {
          throw new Error('token refresh failed')
        }
      }).pipe(
        retry(1),
        catchError((err) => {
          datadogRum.addError(err)

          // exit early and do not log the user out as we have connection problems or
          // hydra is down, but the token may still be valid
          // with this check we want the user to stay logged in in offline scenarios
          if (err instanceof HydraNotReachableError) {
            return of(false)
          }

          // authentication error, log the user out and delete local data
          logout$.next(true)

          getEventSubscription().next({
            type: EventType.Alert,
            notificationType: NotificationType.Alert,
            id: 'session_invalidated',
            options: {
              message: 'Ihre Sitzung ist abgelaufen und Sie müssen sich neu einloggen',
              title: 'Sitzung',
              id: 'session_invalidated',
            },
          })

          return of(false)
        })
      )
    ),
    share()
  )
  requestTokens$.subscribe()

  const requestNewToken = async () => {
    datadogRum.addAction('triggered request token ')
    const prom = firstValueFrom(requestTokens$)
    tokenRefreshSubject$.next(true)
    return prom
  }

  return {
    tokenRefreshSubject$,
    tokens$,
    logout$,
    logoutFinished$,
    requestNewToken,
  }
}
